/**
 * inoRain OTT Pricing Calculator Configuration
 *
 * Single source of truth for tiers, platforms, features, add-ons and the
 * engineering constants behind the bandwidth/storage estimates.
 *
 * PRICING MODEL (inoRain_Pricing_Final):
 *   One platform, 4 tiers, apps & features included by tier.
 *   Monthly, or yearly at -10%. No upfront cost.
 *   Anything not included in a tier can be added on for a monthly fee.
 */

// Tier progression, cheapest to richest. Inclusion is monotonic: a feature
// included from `streamer` is also included in every tier above it.
export const TIER_ORDER = ['starter', 'streamer', 'contentGuru', 'advanced'];

export const APP_CONFIG = {
    // =============================================================================
    // TIER CONFIGURATIONS
    // =============================================================================
    packages: {
        starter: {
            name: 'Starter',
            description: 'For new services launching their first catalogue',
            price: 250,              // Monthly price in USD
            bandwidth: 5,            // TB per month
            storage: 0.5,            // TB total storage (500 GB)
            activeSubscribers: 3000,   // Approx. active subscribers
            color: '#4e6d76'
        },
        streamer: {
            name: 'Streamer',
            description: 'For growing platforms adding mobile and live TV',
            price: 700,
            bandwidth: 10,
            storage: 1,
            activeSubscribers: 6000,
            color: '#0f87b2'
        },
        contentGuru: {
            name: 'Content Guru',
            description: 'For established operators running at scale',
            price: 1200,
            bandwidth: 30,
            storage: 5,
            activeSubscribers: 18000,
            popular: true,
            color: '#00708b'
        },
        advanced: {
            name: 'Advanced',
            description: 'Unlimited capacity, all platforms, and a custom SLA',
            price: null,             // Quote on request
            custom: true,
            bandwidth: Infinity,
            storage: Infinity,
            activeSubscribers: Infinity,
            color: '#1e1b39'
        }
    },

    // =============================================================================
    // PLATFORM APPS
    // =============================================================================
    // Every platform is a flat monthly fee. `includedFrom` names the cheapest
    // tier that bundles it; null means it is always an add-on.
    platforms: {
        web: {
            name: 'Web player',
            price: 0,
            includedFrom: 'starter',
            group: 'Web player',
            icon: '🌐'
        },
        ios: {
            name: 'iOS (iPhone / iPad)',
            price: 150,
            includedFrom: 'streamer',
            group: 'Mobile (iOS + Android)',
            icon: '📱'
        },
        androidMobile: {
            name: 'Android mobile',
            price: 150,
            includedFrom: 'streamer',
            group: 'Mobile (iOS + Android)',
            icon: '📱'
        },
        androidTv: {
            name: 'Android TV',
            price: 150,
            includedFrom: 'contentGuru',
            group: 'Android TV',
            icon: '📺'
        },
        lg: {
            name: 'LG (webOS)',
            price: 150,
            includedFrom: 'contentGuru',
            group: 'LG (webOS) · Samsung (Tizen)',
            icon: '📺'
        },
        samsung: {
            name: 'Samsung (Tizen)',
            price: 150,
            includedFrom: 'contentGuru',
            group: 'LG (webOS) · Samsung (Tizen)',
            icon: '📺'
        },
        appleTv: {
            name: 'Apple TV',
            price: 150,
            includedFrom: 'advanced',
            group: 'Apple TV · Fire TV · Roku',
            icon: '📺'
        },
        fireTv: {
            name: 'Fire TV',
            price: 150,
            includedFrom: 'advanced',
            group: 'Apple TV · Fire TV · Roku',
            icon: '📺'
        },
        roku: {
            name: 'Roku',
            price: 150,
            includedFrom: 'advanced',
            group: 'Apple TV · Fire TV · Roku',
            icon: '📺'
        },
        vidaa: {
            name: 'VIDAA',
            price: 150,
            includedFrom: 'advanced',
            group: 'VIDAA · VIZIO · Whale TV',
            icon: '📺'
        },
        vizio: {
            name: 'VIZIO',
            price: 150,
            includedFrom: 'advanced',
            group: 'VIDAA · VIZIO · Whale TV',
            icon: '📺'
        },
        whaleTv: {
            name: 'Whale TV',
            price: 150,
            includedFrom: 'advanced',
            group: 'VIDAA · VIZIO · Whale TV',
            icon: '📺'
        },
        // Not in the published tier matrix - always quoted as an add-on.
        titanOs: {
            name: 'Titan OS',
            price: 150,
            includedFrom: null,
            group: 'Titan OS',
            icon: '📺'
        },
        desktop: {
            name: 'Desktop (Linux / Mac / Windows)',
            price: 150,
            includedFrom: null,
            group: 'Desktop (Linux / Mac / Windows)',
            icon: '💻'
        }
    },

    // =============================================================================
    // FEATURES
    // =============================================================================
    // `price: 0` + `includedFrom: 'starter'` means it ships with every tier and
    // is never billed. Everything else is selectable as a monthly add-on.
    features: {
        vod: { name: 'Video on Demand', price: 0, includedFrom: 'starter', core: true },
        coinSystem: { name: 'Coin system (per-episode unlock)', price: 0, includedFrom: 'starter', core: true },
        monetization: { name: 'Monetization: SVOD / TVOD / AVOD', price: 0, includedFrom: 'starter', core: true },
        billingGateways: { name: 'Custom billing & payment gateways', price: 0, includedFrom: 'starter', core: true },
        whiteLabel: { name: 'White-label panel & multi-language', price: 0, includedFrom: 'starter', core: true },
        abr: { name: 'Adaptive bitrate streaming', price: 0, includedFrom: 'starter', core: true },
        viewerFeatures: { name: 'Continue watching, favorites, offline, social auth', price: 0, includedFrom: 'starter', core: true },
        analytics: { name: 'Advanced analytics & reporting', price: 0, includedFrom: 'starter', core: true },
        support24h: { name: '24h support — group chat & call', price: 0, includedFrom: 'starter', core: true },

        liveTv: {
            name: 'Live TV, EPG & catchup',
            price: 250,
            includedFrom: 'streamer',
            description: 'Live channels with electronic programme guide and catchup',
            icon: '📡'
        },
        businessPage: {
            name: 'Business & information page',
            price: 80,
            includedFrom: 'streamer',
            description: 'Public marketing and information pages for your service',
            icon: '📄'
        },
        contentProviders: {
            name: 'Content Provider accounts',
            price: 200,
            includedFrom: 'contentGuru',
            description: 'Separate logins so partners can upload and manage their own content',
            icon: '👥'
        },
        promoCodes: {
            name: 'Promo codes',
            price: 80,
            includedFrom: 'contentGuru',
            description: 'Discount and trial codes for campaigns',
            icon: '🎟️'
        },
        entertainment: {
            name: 'Entertainment (games, music, radio)',
            price: 150,
            includedFrom: 'contentGuru',
            description: 'Games, music and radio alongside your video catalogue',
            icon: '🎮'
        },
        aiSuggestions: {
            name: 'AI personalized suggestions',
            price: 200,
            includedFrom: 'advanced',
            description: 'Per-viewer recommendations driven by watch behaviour',
            icon: '🤖'
        },
        marketplace: {
            name: 'Marketplace',
            price: 150,
            includedFrom: 'advanced',
            description: 'In-app storefront for third-party content and services',
            icon: '🛍️'
        }
    },

    // =============================================================================
    // USAGE-BASED ADD-ONS
    // =============================================================================
    // Billed on what you actually use, and never bundled with a tier — these sit
    // outside the tier ladder entirely, so `includedFrom` does not apply.
    // Channel bandwidth is invoiced after the month, on measured usage.
    usageAddOns: {
        ssaiChannelBuilder: {
            name: 'SSAI & Channel Builder',
            unitPrice: 50,
            unit: 'channel / month',
            // Channel traffic is billed separately from the tier allowance and
            // at its own rate, not the per-GB overage rate.
            bandwidthPricePerTB: 4,
            description: 'Server-side ad insertion and channel assembly, per 24/7 channel',
            icon: '📡'
        },
        aiDubbing: {
            name: 'AI dubbing',
            unitPrice: 3.6,
            unit: 'minute',
            description: 'Automatic dubbing into additional languages',
            icon: '🎙️'
        },
        aiSubtitles: {
            name: 'AI subtitles',
            unitPrice: 0.9,
            unit: 'minute',
            description: 'Automatic subtitle generation and translation',
            icon: '💬'
        }
    },

    // =============================================================================
    // CAPACITY OVERAGE
    // =============================================================================
    // Bandwidth and storage beyond the tier allowance are billed per GB.
    overage: {
        bandwidth: {
            name: 'Extra bandwidth',
            pricePerGB: 0.01,        // $10 per TB
            unit: 'GB / month',
            icon: '📶'
        },
        storage: {
            name: 'Extra storage',
            pricePerGB: 0.03,        // $30 per TB
            unit: 'GB',
            icon: '💾'
        }
    },

    // =============================================================================
    // LEGACY ONE-TIME APP PRICING
    // =============================================================================
    // Superseded by monthly platform add-ons. Retained so sales can quote deals
    // that were already scoped under the old upfront model.
    legacyAppBundles: {
        web: { name: 'Web (Browsers)', price: 0, included: true, icon: '🌐' },
        samsung: { name: 'Samsung TV', price: 900, icon: '📺' },
        lg: { name: 'LG TV', price: 1900, icon: '📺' },
        roku: { name: 'Roku', price: 1900, icon: '📺' },
        ios: { name: 'iOS', price: 2200, icon: '📱' },
        androidTv: { name: 'Android TV', price: 1900, icon: '📺' },
        fireTv: { name: 'Fire TV', price: 1900, icon: '📺' },
        androidMobile: { name: 'Android Mobile', price: 2200, icon: '📱' },
        titanOs: { name: 'Titan OS', price: 2000, icon: '📺' },
        whaleTv: { name: 'Whale OS', price: 2000, icon: '📺' },
        desktop: { name: 'Linux/Mac/Windows', price: 3000, icon: '💻' }
    },
    // =============================================================================
    // VIDEO QUALITY CONFIGURATIONS
    // =============================================================================
    videoQualities: {
        SD: {
            name: 'SD',
            resolution: '480p',
            bitrate: '1-2 Mbps',
            multiplier: 1.5,
            description: 'Standard Definition - Basic quality for mobile viewing'
        },
        HD: {
            name: 'HD',
            resolution: '720p',
            bitrate: '3-5 Mbps',
            multiplier: 2.5,
            description: 'High Definition - Good quality for most devices'
        },
        'FULL HD': {
            name: 'FULL HD',
            resolution: '1080p',
            bitrate: '5-8 Mbps',
            multiplier: 4.5,
            description: 'Full High Definition - Excellent quality for TVs and computers'
        },
        MIXED: {
            name: 'MIXED',
            resolution: 'Mobile/TV Average',
            bitrate: '4-6 Mbps',
            multiplier: 3.2,
            description: 'Mixed quality - Average between HD and Full HD for diverse device usage',
            default: true
        },
        '4K': {
            name: '4K',
            resolution: '2160p',
            bitrate: '15-25 Mbps',
            multiplier: 12,
            description: 'Ultra High Definition - Premium quality for 4K displays'
        }
    },
    // =============================================================================
    // CURRENCY CONFIGURATIONS
    // =============================================================================
    currencies: {
        USD: {
            symbol: '$',
            name: 'US Dollar',
            rate: 1, // Base currency
            locale: 'en-US'
        },
        GBP: {
            symbol: '£',
            name: 'British Pound',
            rate: 0.78, // Example rate - should be updated with real rates
            locale: 'en-GB'
        },
        EUR: {
            symbol: '€',
            name: 'Euro',
            rate: 0.85, // Example rate - should be updated with real rates
            locale: 'de-DE'
        }
    },

    // =============================================================================
    // BILLING CONFIGURATIONS
    // =============================================================================
    billing: {
        yearlyDiscount: 0.1, // 10% discount for yearly billing
        roundingRules: {
            largeAmountThreshold: 100, // Amounts above this are rounded to nearest 10
            smallAmountRounding: 1 // Amounts below threshold rounded to nearest whole number
        }
    },
    // =============================================================================
    // DEFAULT VALUES
    // =============================================================================
    defaults: {
        // Usage inputs
        videoAssets: 500,
        avgDuration: 120,           // minutes
        totalPlays: 100,            // concurrent viewers at peak
        avgWatchTime: 30,           // minutes per session
        videoQuality: 'MIXED',

        // Revenue model
        revenueModel: 'SVOD',
        subscribers: 1500,
        avgSpend: 15,               // USD per subscriber per month
        impressions: 1000000,
        cpm: 4,                     // USD per 1000 impressions
        transactions: 1500,
        avgPrice: 5,                // USD per transaction

        // Selections. Web is included everywhere, so it starts on.
        selectedPlatforms: { web: true },
        selectedFeatures: {},

        // Usage-based add-ons, all off until a quantity is entered.
        ssaiChannels: 0,
        ssaiBandwidthTB: 0,
        aiDubbingMinutes: 0,
        aiSubtitleMinutes: 0,

        // Pricing model: 'monthly' is the current no-upfront model.
        // 'legacy' restores one-time app development fees.
        pricingMode: 'monthly',
        appDiscount: 0,             // % off legacy one-time app costs

        // Growth rate for projections
        monthlyGrowthRate: 3,       // % per month

        // UI defaults
        billingType: 'monthly',
        currency: 'USD',
        showProjections: true
    },
    // =============================================================================
    // REVENUE MODELS
    // =============================================================================
    revenueModels: {
        SVOD: {
            name: 'SVOD (Subscription)',
            description: 'Subscription Video on Demand - Monthly/yearly subscriptions',
            fields: ['subscribers', 'avgSpend']
        },
        AVOD: {
            name: 'AVOD (Advertising)',
            description: 'Advertising Video on Demand - Revenue from advertisements',
            fields: ['impressions', 'cpm']
        },
        TVOD: {
            name: 'TVOD (Transactional)',
            description: 'Transactional Video on Demand - Pay per view/rent/purchase',
            fields: ['transactions', 'avgPrice']
        },
        HYBRID: {
            name: 'HYBRID',
            description: 'Combination of subscription and advertising revenue',
            fields: ['subscribers', 'avgSpend', 'impressions', 'cpm']
        }
    },

    // =============================================================================
    // CALCULATION SETTINGS (Configurable by users)
    // =============================================================================
    calculationSettings: {
        // Core streaming bitrates (based on 200+ content owners data)
        streamingBitrates: {
            SD: {
                value: 1.2,
                description: 'Average streaming bitrate for SD (480p) quality videos - optimized for mobile',
                min: 0.8,
                max: 2.0,
                step: 0.1,
                unit: 'Mbps per stream',
                calculation: 'Formula: monthlyBandwidthGB = totalPlays × (avgWatchTime/60) × bitrate × (30×24) × 0.125 × (1/efficiency) × peakFactor'
            },
            HD: {
                value: 3.2,
                description: 'Average streaming bitrate for HD (720p) quality videos - most common quality',
                min: 2.5,
                max: 4.5,
                step: 0.1,
                unit: 'Mbps per stream',
                calculation: 'Formula: monthlyBandwidthGB = totalPlays × (avgWatchTime/60) × bitrate × (30×24) × 0.125 × (1/efficiency) × peakFactor'
            },
            'FULL HD': {
                value: 5.8,
                description: 'Average streaming bitrate for Full HD (1080p) quality videos - premium quality',
                min: 4.5,
                max: 8.0,
                step: 0.1,
                unit: 'Mbps per stream',
                calculation: 'Formula: monthlyBandwidthGB = totalPlays × (avgWatchTime/60) × bitrate × (30×24) × 0.125 × (1/efficiency) × peakFactor'
            },
            MIXED: {
                value: 4.2,
                description: 'Average bitrate for mixed device usage (mobile + TV + desktop) - real-world average',
                min: 3.0,
                max: 6.0,
                step: 0.1,
                unit: 'Mbps per stream',
                calculation: 'Formula: monthlyBandwidthGB = totalPlays × (avgWatchTime/60) × bitrate × (30×24) × 0.125 × (1/efficiency) × peakFactor'
            },
            '4K': {
                value: 18,
                description: 'Average streaming bitrate for 4K (2160p) quality videos - premium tier',
                min: 15,
                max: 30,
                step: 0.5,
                unit: 'Mbps per stream',
                calculation: 'Formula: monthlyBandwidthGB = totalPlays × (avgWatchTime/60) × bitrate × (30×24) × 0.125 × (1/efficiency) × peakFactor'
            }
        },

        // Storage requirements per hour of content (based on real production data)
        storageRequirements: {
            SD: {
                value: 0.6,
                description: 'Storage space required per hour of SD (480p) video content - efficient compression',
                min: 0.4,
                max: 1.0,
                step: 0.05,
                unit: 'GB per hour',
                calculation: 'Formula: totalStorageGB = videoAssets × (avgDuration/60) × storagePerHour × storageOverhead'
            },
            HD: {
                value: 1.3,
                description: 'Storage space required per hour of HD (720p) video content - standard quality',
                min: 1.0,
                max: 2.0,
                step: 0.05,
                unit: 'GB per hour',
                calculation: 'Formula: totalStorageGB = videoAssets × (avgDuration/60) × storagePerHour × storageOverhead'
            },
            'FULL HD': {
                value: 2.4,
                description: 'Storage space required per hour of Full HD (1080p) video content - optimized encoding',
                min: 2.0,
                max: 3.5,
                step: 0.1,
                unit: 'GB per hour',
                calculation: 'Formula: totalStorageGB = videoAssets × (avgDuration/60) × storagePerHour × storageOverhead'
            },
            MIXED: {
                value: 1.8,
                description: 'Average storage per hour for mixed quality content library - real-world average',
                min: 1.3,
                max: 2.8,
                step: 0.1,
                unit: 'GB per hour',
                calculation: 'Formula: totalStorageGB = videoAssets × (avgDuration/60) × storagePerHour × storageOverhead'
            },
            '4K': {
                value: 7.2,
                description: 'Storage space required per hour of 4K (2160p) video content - modern codecs',
                min: 5.5,
                max: 12.0,
                step: 0.25,
                unit: 'GB per hour',
                calculation: 'Formula: totalStorageGB = videoAssets × (avgDuration/60) × storagePerHour × storageOverhead'
            }
        },

        // Real-world efficiency factors (calibrated from 200+ content owners)
        efficiencyFactors: {
            bandwidthEfficiency: {
                value: 0.78,
                description: 'Network efficiency factor - real-world TCP overhead, buffering, adaptive bitrate, retransmissions',
                min: 0.65,
                max: 0.90,
                step: 0.01,
                unit: 'efficiency %',
                calculation: 'Formula: inefficiencyFactor = 1 / bandwidthEfficiency (applied to base bandwidth calculation)'
            },
            peakUsageFactor: {
                value: 1.45,
                description: 'Peak usage multiplier - based on actual concurrent viewing patterns and time zones',
                min: 1.1,
                max: 2.2,
                step: 0.05,
                unit: 'peak multiplier',
                calculation: 'Formula: finalBandwidth = baseBandwidth × peakUsageFactor (multiplied at end of calculation)'
            },
            cdnCacheHitRate: {
                value: 0.82,
                description: 'CDN cache hit rate - average across major CDN providers (Cloudflare, AWS, Akamai)',
                min: 0.6,
                max: 0.95,
                step: 0.01,
                unit: 'cache hit %',
                calculation: 'Formula: originBandwidth = totalBandwidth × (1 - cdnCacheHitRate) (for CDN cost calculation)'
            },
            storageOverhead: {
                value: 1.28,
                description: 'Storage overhead - includes multiple quality versions, backups, transcoding files, thumbnails, metadata',
                min: 1.15,
                max: 1.6,
                step: 0.01,
                unit: 'overhead multiplier',
                calculation: 'Formula: totalStorage = contentStorage × storageOverhead (applied after base calculation)'
            }
        },

        // Growth and projection settings
        projectionSettings: {
            defaultGrowthRate: {
                value: 3,
                description: 'Default monthly revenue growth rate for projections',
                min: 0,
                max: 20,
                step: 0.5,
                unit: '% per month',
                calculation: 'Formula: quarterlyRevenue = revenue × Math.pow(growthMultiplier, index × 3) × 3'
            },
            userGrowthCorrelation: {
                value: 0.8,
                description: 'How much user growth correlates with revenue growth (0.8 = 80%)',
                min: 0.3,
                max: 1.0,
                step: 0.05,
                unit: 'correlation factor',
                calculation: 'Formula: bandwidthGrowth = revenueGrowth × userGrowthCorrelation (for future scaling)'
            }
        }
    },
    // =============================================================================
    // TOOLTIPS AND HELP TEXT
    // =============================================================================
    tooltips: {
        videoAssets: 'Total number of unique video files in your content library',
        avgDuration: 'Average length of your video content in minutes',
        totalPlays: 'Number of users simultaneously watching at peak times',
        avgWatchTime: 'Average time a user spends watching per session',
        videoQuality: 'Video quality drives both bandwidth and storage requirements',
        subscribers: 'Number of paying subscribers for the SVOD model',
        avgSpend: 'Average monthly revenue per subscriber',
        impressions: 'Total monthly ad impressions for the AVOD model',
        cpm: 'Revenue per 1000 ad impressions',
        transactions: 'Number of pay-per-view or rental transactions per month',
        avgPrice: 'Average price per TVOD transaction',
        platforms: 'Apps are included by tier. Any platform your tier does not include can be added for a flat monthly fee.',
        features: 'Features are included by tier. Anything not included can be added on individually.',
        overage: 'Bandwidth and storage beyond your tier allowance are billed per GB.',
        activeSubscribers: 'A rough guide based on typical usage. Real capacity varies with video quality and watch time.',
        usageAddOns: 'Billed on measured usage and never bundled with a tier, so these cost the same on every plan.',
        monthlyGrowthRate: 'Expected monthly growth rate used for revenue projections',
        pricingMode: 'Monthly puts apps inside the subscription with no upfront cost. Legacy restores one-time app development fees.'
    }
};

// =============================================================================
// HELPERS
// =============================================================================

/** Position of a tier in the upgrade path, or -1 if unknown. */
export const tierRank = (tierKey) => TIER_ORDER.indexOf(tierKey);

/**
 * Is `item` (a platform or feature) bundled with `tierKey`?
 * Inclusion is monotonic up the tier order.
 */
export const isIncludedInTier = (item, tierKey) => {
    if (!item || !item.includedFrom) return false;
    const from = tierRank(item.includedFrom);
    const tier = tierRank(tierKey);
    return from !== -1 && tier !== -1 && tier >= from;
};

/** 'included' | 'addon' — how `item` is delivered on `tierKey`. */
export const tierAvailability = (item, tierKey) =>
    isIncludedInTier(item, tierKey) ? 'included' : 'addon';

/** Monthly cost of `item` on `tierKey`: zero when the tier already bundles it. */
export const itemCostForTier = (item, tierKey) =>
    isIncludedInTier(item, tierKey) ? 0 : (item?.price || 0);

/** Platform rows grouped exactly as the published pricing sheet presents them. */
export const getPlatformGroups = () => {
    const groups = [];
    Object.entries(APP_CONFIG.platforms).forEach(([key, platform]) => {
        const existing = groups.find(g => g.group === platform.group);
        if (existing) {
            existing.keys.push(key);
        } else {
            groups.push({ group: platform.group, keys: [key], includedFrom: platform.includedFrom });
        }
    });
    return groups;
};

/** Features that ship with every tier and are never billed. */
export const getCoreFeatures = () =>
    Object.entries(APP_CONFIG.features).filter(([, f]) => f.core);

/** Features that can be bought individually. */
export const getOptionalFeatures = () =>
    Object.entries(APP_CONFIG.features).filter(([, f]) => !f.core);

/** Platforms that can be bought individually (everything except free web). */
export const getBillablePlatforms = () =>
    Object.entries(APP_CONFIG.platforms).filter(([, p]) => p.price > 0);

// Helper function to get default video quality
export const getDefaultVideoQuality = () => {
    return Object.keys(APP_CONFIG.videoQualities).find(
        quality => APP_CONFIG.videoQualities[quality].default
    ) || 'MIXED';
};

// Helper function to get currency symbol
export const getCurrencySymbol = (currencyCode) => {
    return APP_CONFIG.currencies[currencyCode]?.symbol || '$';
};

// Helper function to get video quality options for dropdown
export const getVideoQualityOptions = () => {
    return Object.entries(APP_CONFIG.videoQualities).map(([key, quality]) => ({
        value: key,
        label: `${quality.name} → ${quality.resolution} → ${quality.bitrate}`,
        multiplier: quality.multiplier
    }));
};

/** Format a TB figure the way the pricing sheet does (500 GB, not 0.5 TB). */
export const formatCapacity = (tb) => {
    if (tb === Infinity) return 'Custom';
    if (tb < 1) return `${Math.round(tb * 1000)} GB`;
    return `${Number(tb.toFixed(2))} TB`;
};

export default APP_CONFIG;
